import { PageType } from '@app-types/PageType'
import LoadingSpinner from '@components/shared/Loading/Loading'
import FocusModeOverlay from '@components/shared/PaginationAndSettingsRow/FocusModeOverlay'
import NoSleepManager from '@components/shared/PaginationAndSettingsRow/NoSleepManager'
import QuickActions from '@components/shared/PaginationAndSettingsRow/QuickActions'
import SwipeIndicator from '@components/shared/PaginationAndSettingsRow/SwipeIndicator'
import useAutoScroll from '@hooks/useAutoScroll'
import { useFocusMode } from '@hooks/useFocusMode'
import usePagination from '@hooks/usePage'
import useReadingProgress from '@hooks/useReadingProgress'
import classNames from 'classnames'
import { useCallback, useRef, type FC, type ReactNode } from 'react'
import toast from 'react-hot-toast'
import DuaBottomDock from './DuaBottomDock'
import DuaBottomPagination from './DuaBottomPagination'
import DuaCompletionSection from './DuaCompletionSection'
import DuaHeaderCard from './DuaHeaderCard'
import DuaPageComponent, { type DuaPartType } from './DuaPageComponent'
import { CevsenCompact } from './CevsenCompact'
import { useDockScrollPosition } from './hooks/useDockScrollPosition'
import { useDuaSwipeNavigation } from './hooks/useDuaSwipeNavigation'

export type DuaReaderContainerProps = {
  /** Dua metadata descriptor */
  dua: {
    name: string
    type: PageType
    pageCount: number
  }
  /** Current page number */
  page: number
  /** Dua items, or Cevşen items grouped by bâb, for the current page. */
  data: DuaPartType[] | Record<string, DuaPartType[]>
  /** Whether running inside native mobile WebView */
  mobileReader?: boolean
  /** Keeps the dock mounted while native page content is loading. */
  isContentReady?: boolean
  /** Base path for pagination routing (default: '/dualar') */
  basePath?: string
  /** Whether audio player is present on the page */
  hasAudio?: boolean
  /** Optional extra content to render beneath the header card, such as video player */
  children?: ReactNode
}

/**
 * Main reader container coordinating dua pagination, gestures, auto-scrolling,
 * focus mode, reading progress, header navigation, and the floating bottom dock.
 */
const DuaReaderContainer: FC<DuaReaderContainerProps> = ({
  dua,
  page,
  data,
  mobileReader = false,
  isContentReady = true,
  basePath = '/dualar',
  hasAudio = false,
  children,
}) => {
  // Focus mode state
  const { isFocusMode, toggleFocusMode } = useFocusMode()
  const focusScrollContainerRef = useRef<HTMLDivElement>(null)

  // Track scrolling near the bottom stats area to dock the bar
  const { sentinelRef, isDockedAtBottom } = useDockScrollPosition()

  // Page pagination state and navigation handlers
  const { nextPage, previousPage, goToPage } = usePagination({
    path: dua.type,
    max: dua.pageCount,
    min: 1,
    basePath,
    mobileReader,
  })

  // Touch swipe gesture navigation
  const { swipeState } = useDuaSwipeNavigation(nextPage, previousPage)

  // Auto-scrolling controller
  const { isScrolling, isPending, toggleScroll, duration, setDuration } = useAutoScroll(
    dua.type,
    focusScrollContainerRef,
    isFocusMode,
    isContentReady,
  )

  /** Displays a custom toast notification when bookmarking */
  const notifyBookmark = useCallback((message = 'Kalınan yer kaydedildi!') => {
    toast.custom(
      (t) => (
        <div
          className="flex justify-between items-center bg-slate-800 p-2.5 rounded-xl text-white shadow-lg text-sm"
          onClick={() => toast.dismiss(t.id)}
        >
          🔖 {message}
        </div>
      ),
      {
        duration: 1500,
        position: 'top-center',
      },
    )
  }, [])

  // Reading progress and page bookmarking
  const { userData, updateUserData } = useReadingProgress(dua.type, page, notifyBookmark)

  const isCurrentPageMarked = Number(userData[dua.type]) === Number(page)

  /** Toggles bookmark for the active reading page */
  const handleTogglePageBookmark = () => {
    if (isCurrentPageMarked) {
      updateUserData(dua.type, 0)
      notifyBookmark('Kalınan yer kaldırıldı!')
    } else {
      updateUserData(dua.type, Number(page))
      notifyBookmark('Kalınan yer kaydedildi!')
    }
  }

  /** Calculates total read page count relative to last saved bookmark */
  const calculateReadPageCount = () => {
    const isLastPage = Number(page) === dua.pageCount
    const lastRead = (userData[dua.type] as number) || 0
    return isLastPage ? Number(page) - lastRead + 1 : Number(page) - lastRead
  }

  const quickActionsRenderData = {
    isScrolling,
    isPending,
    toggleScroll,
    basePath,
    type: dua.type,
    isMarked: isCurrentPageMarked,
    handleBookmark: handleTogglePageBookmark,
    getReadPageCount: calculateReadPageCount,
    page,
  }

  /** Preserves grouped Cevşen content in both normal and focus mode. */
  const renderReadingContent = () =>
    !isContentReady ? (
      <LoadingSpinner />
    ) : Array.isArray(data) ? (
      <DuaPageComponent type={dua.type} data={data} page={page} />
    ) : (
      <CevsenCompact data={data} page={page} />
    )

  return (
    <div
      className={classNames('relative', {
        'pb-2 sm:pb-4': isDockedAtBottom,
        'pb-16 sm:pb-20': !isDockedAtBottom && hasAudio,
        'pb-10 sm:pb-12': !isDockedAtBottom && !hasAudio,
      })}
    >
      {/* Screen sleep inhibitor during reading */}
      <NoSleepManager />

      {/* Header Card with Navigation */}
      <DuaHeaderCard
        title={dua.name}
        page={page}
        maxPage={dua.pageCount}
        onPreviousPage={previousPage}
        onNextPage={nextPage}
        isFirstPage={Number(page) <= 1}
        isLastPage={Number(page) >= dua.pageCount}
      />

      {/* Optional video or additional content */}
      {children}

      {/* Dua recitation item cards */}
      {renderReadingContent()}

      {/* Sentinel element to trigger docking as soon as user scrolls to bottom navigation */}
      <div
        ref={sentinelRef}
        className="h-1 w-full"
        hidden={!isContentReady}
        aria-hidden="true"
      />

      {/* Bottom pagination buttons for convenient in-page navigation */}
      {isContentReady && (
        <DuaBottomPagination
          page={page}
          maxPage={dua.pageCount}
          minPage={1}
          onPreviousPage={previousPage}
          onNextPage={nextPage}
          onGoToPage={goToPage}
          isFirstPage={Number(page) <= 1}
          isLastPage={Number(page) >= dua.pageCount}
        />
      )}

      {/* Completion & statistics section at bottom of content */}
      {isContentReady && (
        <DuaCompletionSection
          type={dua.type}
          page={page}
          maxPage={dua.pageCount}
          lastReadPage={Number(userData[dua.type]) || 0}
          onGoToFirstPage={() => goToPage(1)}
        />
      )}

      {/* Bottom Reader Dock - Floats above viewport, or docks naturally after stats when scrolled to bottom */}
      <DuaBottomDock
        isDocked={isDockedAtBottom}
        hasAudio={hasAudio}
        page={page}
        maxPage={dua.pageCount}
        minPage={1}
        onGoToPage={goToPage}
        isScrolling={isScrolling}
        isPending={isPending}
        onToggleScroll={toggleScroll}
        isPageMarked={isCurrentPageMarked}
        onToggleBookmark={handleTogglePageBookmark}
        isFocusMode={isFocusMode}
        onToggleFocusMode={toggleFocusMode}
        type={dua.type}
        autoScrollDuration={duration}
        setAutoScrollDuration={setDuration}
        quickActions={<QuickActions {...quickActionsRenderData} />}
      />

      {/* Distraction-free Full Screen Focus Mode */}
      {isFocusMode && (
        <FocusModeOverlay
          onExit={toggleFocusMode}
          scrollContainerRef={focusScrollContainerRef}
        >
          {renderReadingContent()}
        </FocusModeOverlay>
      )}

      {/* Swipe navigation visual indicator */}
      <SwipeIndicator swipeState={swipeState} />
    </div>
  )
}

export default DuaReaderContainer
